import React from "react"
import { Link } from "react-router-dom"
import { ArrowRight } from "lucide-react"
import { useProjects } from "../../hooks/useProjects"

function ProjectCard({ project, className = "" }) {
  return (
    <Link
      to={`/works/${project.slug}`}
      className={`group relative overflow-hidden cursor-pointer block ${className}`}
    >
      <div className="w-full h-full">
        <img
          src={project.thumbnail}
          alt={project.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-stone-950/20 to-transparent opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
      {/* Content */}
      <div className="absolute inset-0 p-6 flex flex-col justify-end translate-y-3 group-hover:translate-y-0 transition-transform duration-500">
        <span className="font-['Space_Grotesk'] text-[9px] tracking-[0.3em] text-amber-400 uppercase mb-2">
          {project.category} — {project.year}
        </span>
        <h3 className="font-['Cormorant_Garamond'] text-xl lg:text-2xl font-light text-stone-100 leading-tight mb-1">
          {project.title}
        </h3>
        <p className="font-['Inter'] text-xs text-stone-400 mb-4">{project.location}</p>
        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100">
          <span className="font-['Space_Grotesk'] text-[10px] tracking-[0.2em] text-amber-400">VIEW PROJECT</span>
          <ArrowRight size={12} className="text-amber-400" />
        </div>
      </div>
    </Link>
  )
}

// Skeleton placeholder for loading state
function SkeletonCard({ className = "" }) {
  return (
    <div className={`animate-pulse bg-stone-800 ${className}`} />
  )
}

export default function FeaturedProjects() {
  const { projects, loading } = useProjects()
  const featured = projects.slice(0, 5)

  return (
    <section className="py-24 lg:py-32 bg-stone-950">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-4">
              <span className="w-6 h-px bg-amber-400" />
              Selected Works
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-4xl lg:text-6xl font-light text-stone-100 leading-tight">
              Projects that<br />
              <span className="italic text-stone-400">define excellence</span>
            </h2>
          </div>
          <Link
            to="/works"
            className="inline-flex items-center gap-3 font-['Space_Grotesk'] text-[11px] tracking-[0.2em] text-stone-400 hover:text-amber-400 transition-colors group self-start lg:self-auto"
          >
            ALL PROJECTS
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 auto-rows-[280px]">
          {loading ? (
            <>
              <SkeletonCard className="lg:row-span-2" />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : (
            <>
              {featured[0] && <ProjectCard project={featured[0]} className="lg:row-span-2" />}
              {featured[1] && <ProjectCard project={featured[1]} />}
              {featured[2] && <ProjectCard project={featured[2]} />}
              {featured[3] && <ProjectCard project={featured[3]} />}
              {featured[4] && <ProjectCard project={featured[4]} />}
            </>
          )}
        </div>
      </div>
    </section>
  )
}
